import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, FileText, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicDocumentETA = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);
  const [service, setService] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    document_eta: '',
    notes: ''
  });
  const [linkGeneratedDate, setLinkGeneratedDate] = useState(null);

  useEffect(() => {
    fetchLeadDetails();
  }, [token]);

  const fetchLeadDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/document-eta/${token}`);
      setLead(response.data.lead);
      setService(response.data.service);
      
      // Capture when the welcome email was sent (link generated)
      if (response.data.lead.welcome_email_sent_at) {
        setLinkGeneratedDate(new Date(response.data.lead.welcome_email_sent_at));
      }
      
      // Check if already submitted
      if (response.data.lead.document_eta) {
        setSubmitted(true);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching lead:', err);
      setError(err.response?.data?.detail || 'Invalid or expired link');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.document_eta) {
      alert('Please select a date for when you expect to have all documents ready.');
      return;
    }
    
    // Validate date is within 2 weeks from link generation
    const selectedDate = new Date(formData.document_eta);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const maxDate = linkGeneratedDate ? new Date(linkGeneratedDate) : new Date();
    maxDate.setDate(maxDate.getDate() + 14); // 2 weeks from link generation
    
    if (selectedDate < today) {
      alert('Please select a date in the future.');
      return;
    }
    
    if (selectedDate > maxDate) {
      alert('Please select a date within 2 weeks from when you received this link.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/public/document-eta/${token}`, formData);
      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting ETA:', err);
      alert(err.response?.data?.detail || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <p style={{ fontSize: '18px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '12px', maxWidth: '500px', textAlign: 'center' }}>
          <AlertCircle size={64} color="#ef4444" style={{ marginBottom: '20px' }} />
          <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>Invalid Link</h2>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>{error}</p>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>Please contact your process consultant for assistance.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '12px', maxWidth: '600px', textAlign: 'center' }}>
          <CheckCircle size={64} color="#10b981" style={{ marginBottom: '20px' }} />
          <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>Thank You!</h2>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '16px' }}>
            Your document submission timeline has been recorded successfully.
          </p>
          <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ color: '#4b5563', marginBottom: '8px', fontSize: '14px' }}>Expected Submission Date:</p>
            <p style={{ color: '#777db0', fontSize: '20px', fontWeight: 'bold' }}>
              {lead.document_eta ? new Date(lead.document_eta).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              }) : new Date(formData.document_eta).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            Your process consultant <strong>{lead.process_consultant_name}</strong> will reach out to you closer to this date.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '30px', color: 'white', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>📅 Document Submission Timeline</h1>
          <p style={{ margin: 0, fontSize: '16px', opacity: 0.9 }}>Help us plan your visa application</p>
        </div>

        {/* Content */}
        <div style={{ padding: '40px' }}>
          {/* Client Info */}
          <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#1f2937', fontSize: '18px' }}>Application Details</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Name:</span>
                <strong style={{ color: '#1f2937', fontSize: '14px' }}>{lead.name}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Service:</span>
                <strong style={{ color: '#1f2937', fontSize: '14px' }}>{lead.service_type}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Destination:</span>
                <strong style={{ color: '#1f2937', fontSize: '14px' }}>{lead.destination_country}</strong>
              </div>
            </div>
          </div>

          {/* Document Checklist */}
          {service && service.document_checklist && service.document_checklist.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1f2937', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} color="#777db0" />
                Required Documents
              </h3>
              <div style={{ background: '#f3f4f6', padding: '15px', borderRadius: '8px' }}>
                {service.document_checklist.map((item, index) => (
                  <div key={index} style={{ 
                    padding: '10px',
                    marginBottom: index < service.document_checklist.length - 1 ? '8px' : 0,
                    background: 'white',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    borderLeft: `3px solid ${item.required ? '#10b981' : '#6b7280'}`
                  }}>
                    <span style={{ fontSize: '18px' }}>{item.required ? '✅' : '○'}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: '#1f2937', fontSize: '14px' }}>{item.item}</span>
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: item.required ? '#10b981' : '#6b7280', fontWeight: '600' }}>
                        ({item.required ? 'REQUIRED' : 'OPTIONAL'})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ETA Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '12px', color: '#1f2937', fontWeight: '600', fontSize: '16px' }}>
                <Calendar size={18} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle' }} />
                When will you have all your documents ready? *
              </label>
              <input
                type="date"
                value={formData.document_eta}
                onChange={(e) => setFormData({ ...formData, document_eta: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                max={linkGeneratedDate ? new Date(new Date(linkGeneratedDate).setDate(new Date(linkGeneratedDate).getDate() + 14)).toISOString().split('T')[0] : new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0]}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#777db0'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <small style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginTop: '8px' }}>
                Please choose a date within the next 2 weeks. We know you're eager to get started, and honestly, the sooner you can gather everything, the better. It gives us more time to review your application thoroughly and address any concerns that might come up.
              </small>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#1f2937', fontWeight: '600', fontSize: '16px' }}>
                Any questions or concerns?
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Feel free to let us know if you're having trouble with any particular document, or if you have questions about what we need. We're here to help."
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#777db0'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                fontWeight: '600',
                color: 'white',
                background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              }}
              onMouseEnter={(e) => !submitting && (e.target.style.transform = 'translateY(-2px)', e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)')}
              onMouseLeave={(e) => !submitting && (e.target.style.transform = 'translateY(0)', e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)')}
            >
              {submitting ? 'Submitting...' : '✓ Submit Timeline'}
            </button>
          </form>

          {/* Process Consultant Info */}
          <div style={{ marginTop: '30px', padding: '20px', background: '#ede9fe', borderRadius: '8px', border: '1px solid #c4b5fd' }}>
            <p style={{ margin: '0 0 15px 0', color: '#5b21b6', fontSize: '14px', fontWeight: '600' }}>
              Your Process Consultant:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>👤</span>
                <span style={{ color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                  {lead.process_consultant_name || 'Not Assigned'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>📧</span>
                <a href={`mailto:${lead.process_consultant}`} style={{ color: '#6366f1', fontSize: '14px', textDecoration: 'none' }}>
                  {lead.process_consultant}
                </a>
              </div>
              {lead.process_consultant_phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>📱</span>
                  <a href={`tel:${lead.process_consultant_phone}`} style={{ color: '#6366f1', fontSize: '14px', textDecoration: 'none' }}>
                    {lead.process_consultant_phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '30px', color: 'white', opacity: 0.8, fontSize: '14px' }}>
        <p>Secured by Visaasure • Your trusted visa partner</p>
      </div>
    </div>
  );
};

export default PublicDocumentETA;
